import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";

export default function GrowthJourney() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/milestones").then((r) => setItems(r.data || [])); }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((m) => {
      const era = m.era || m.date_label || "Recent";
      if (!map.has(era)) map.set(era, []);
      map.get(era).push(m);
    });
    return Array.from(map.entries());
  }, [items]);

  return (
    <div data-testid="journey-page" className="section-pad">
      <div className="container-pp">
        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="editorial-divider mb-5" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Pop Panda · Growth Journey</div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="editorial-h1 text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.95]">
              A young brand,<br />
              <em className="font-display italic">documenting its growth</em><br />
              honestly.
            </motion.h1>
          </div>
          <div className="lg:col-span-4">
            <div className="card-elevated p-6 bg-brand-yellow/15 border border-brand-yellow/40">
              <Quote size={18} className="text-brand-red" />
              <p className="mt-3 font-display text-xl leading-snug">From a small kitchen to your nearest counter — every milestone here is real, and dated.</p>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-5 border-y border-brand-line py-8">
          {[
            { n: items.length, l: "Documented milestones" },
            { n: "2024", l: "Year founded" },
            { n: "Home", l: "First kitchen" },
            { n: "1", l: "Stick at a time" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-4xl md:text-5xl text-brand-charcoal">{s.n}</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mt-2">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="mt-20 space-y-24" data-testid="journey-timeline">
          {grouped.length === 0 && (
            <div className="text-brand-charcoal/60">Our timeline is being written — check back soon.</div>
          )}
          {grouped.map(([era, list], gi) => (
            <section key={era} className="grid lg:grid-cols-12 gap-10">
              {/* Era heading column */}
              <div className="lg:col-span-3 lg:sticky lg:top-32 self-start">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Era · {gi + 1}</div>
                <div className="font-display text-5xl md:text-6xl mt-3 leading-none">{era}</div>
                <div className="editorial-divider mt-5" />
                <div className="text-xs text-brand-charcoal/55 mt-3">{list.length} milestone{list.length !== 1 ? "s" : ""}</div>
              </div>

              {/* Items column */}
              <ol className="lg:col-span-9 relative border-l-2 border-brand-charcoal/15 ml-2 space-y-14">
                {list.map((m, i) => {
                  const reversed = i % 2 === 1;
                  return (
                    <motion.li
                      key={m.id}
                      data-testid={`journey-${m.id}`}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 0.5, delay: i * 0.05 }}
                      className="pl-8 relative"
                    >
                      <span className="absolute -left-[11px] top-1.5 h-5 w-5 rounded-full bg-brand-yellow border-2 border-brand-charcoal flex items-center justify-center">
                        <Sparkles size={9} className="text-brand-charcoal" />
                      </span>
                      <div className={`grid ${m.image_url ? "md:grid-cols-2" : ""} gap-6 items-start`}>
                        <div className={reversed && m.image_url ? "md:order-2" : ""}>
                          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">{m.date_label}</div>
                          <h3 className="font-display text-3xl md:text-4xl mt-2 leading-tight">{m.title}</h3>
                          {m.body && <p className="mt-3 text-brand-charcoal/70 leading-relaxed">{m.body}</p>}
                        </div>
                        {m.image_url && (
                          <div className={reversed ? "md:order-1" : ""}>
                            <div className="overflow-hidden rounded-2xl aspect-[16/10]">
                              <img src={m.image_url} alt={m.title} className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.li>
                  );
                })}
              </ol>
            </section>
          ))}
        </div>

        {/* CTA band */}
        <div className="mt-24 rounded-3xl p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-6 bg-brand-charcoal text-brand-cream relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-brand-yellow/15 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-yellow flex items-center gap-2"><Sparkles size={14} /> Be part of the next milestone</div>
            <h3 className="font-display text-4xl md:text-5xl mt-3 leading-none text-white">Your idea could be on this wall.</h3>
            <p className="mt-3 text-brand-cream max-w-md">Submit a flavour. Solve a challenge. Become a campus ambassador. We document who helps us — by name.</p>
          </div>
          <Link to="/flavour-creator" data-testid="journey-cta" className="btn-pill btn-primary relative">Create Your Own Popsicle <ArrowUpRight size={16} className="arrow" /></Link>
        </div>
      </div>
    </div>
  );
}
